import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext.tsx';
import './AdminJournals.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8093/api';
const PAGE_SIZE = 20;

interface JournalUser {
  userId:        number;
  name:          string;
  email:         string;
  totalTrades:   number;
  totalPnl:      number;
  lastTradeDate: string;
}

interface PagedData {
  content:       JournalUser[];
  totalElements: number;
  totalPages:    number;
  page:          number;
  size:          number;
}

type SortField = 'lastTrade' | 'totalTrades' | 'totalPnl';
type SortDir   = 'asc' | 'desc';

const fmtPnl = (v: number) => {
  const sign = v >= 0 ? '+₹' : '-₹';
  const abs  = Math.abs(v);
  if (abs >= 100_000) return sign + (abs / 100_000).toFixed(1) + 'L';
  if (abs >= 1_000)   return sign + (abs / 1_000).toFixed(1) + 'K';
  return sign + abs.toLocaleString('en-IN');
};

const fmtDate = (ds: string) => {
  if (!ds) return '—';
  const [y, m, d] = ds.substring(0, 10).split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
};

const AdminJournals: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();

  const isAdmin = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    return user.roles?.some((r: string) => r.toUpperCase().includes('ADMIN'));
  }, [isAuthenticated, user]);

  const [paged,   setPaged]   = useState<PagedData | null>(null);
  const [loading, setLoading] = useState(false);
  const [sort,    setSort]    = useState<SortField>('lastTrade');
  const [dir,     setDir]     = useState<SortDir>('desc');
  const [page,    setPage]    = useState(1);

  useEffect(() => {
    if (!isLoading && !isAdmin) { toast.error('Access denied'); navigate('/'); }
  }, [isLoading, isAdmin, navigate]);

  const fetchPage = async (s: SortField, d: SortDir, p: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res   = await fetch(
        `${API_BASE_URL}/admin/journal/users?sort=${s}&dir=${d}&page=${p}&size=${PAGE_SIZE}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const json = await res.json();
      setPaged(json.data);
    } catch {
      toast.error('Failed to load journal users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) fetchPage(sort, dir, page); }, [isAdmin]);

  const handleSort = (field: SortField) => {
    const newDir: SortDir = sort === field && dir === 'desc' ? 'asc' : 'desc';
    setSort(field);
    setDir(newDir);
    setPage(1);
    fetchPage(field, newDir, 1);
  };

  const goToPage = (p: number) => {
    setPage(p);
    fetchPage(sort, dir, p);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sort !== field) return <FaSort className="sort-icon muted" />;
    return dir === 'desc'
      ? <FaSortDown className="sort-icon active" />
      : <FaSortUp   className="sort-icon active" />;
  };

  if (isLoading) return <div className="main-content"><div className="loading">Checking permissions...</div></div>;
  if (!isAdmin)  return null;

  const users      = paged?.content       ?? [];
  const totalPages = paged?.totalPages    ?? 0;
  const total      = paged?.totalElements ?? 0;
  const startIdx   = ((page - 1) * PAGE_SIZE) + 1;
  const endIdx     = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="main-content">
      <div className="admin-header">
        <button className="back-btn" onClick={() => navigate(-1)}><FaArrowLeft /></button>
        <h2 className="admin-title">Trade Journals</h2>
      </div>

      {!loading && paged && (
        <div className="aj-meta">
          <span className="aj-count">
            {total} users have logged trades
            {total > 0 && ` · showing ${startIdx}–${endIdx}`}
          </span>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : users.length === 0 ? (
        <div className="no-data">No trades logged yet.</div>
      ) : (
        <>
          <div className="admin-table-container">
            <table className="admin-table aj-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th className="sortable" onClick={() => handleSort('totalTrades')}>
                    Total Trades <SortIcon field="totalTrades" />
                  </th>
                  <th className="sortable" onClick={() => handleSort('totalPnl')}>
                    Total P&amp;L <SortIcon field="totalPnl" />
                  </th>
                  <th className="sortable" onClick={() => handleSort('lastTrade')}>
                    Last Trade <SortIcon field="lastTrade" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr
                    key={u.userId}
                    className="aj-row"
                    onClick={() => navigate(`/admin/journals/${u.userId}`, { state: { name: u.name, email: u.email } })}
                  >
                    <td className="aj-num">{startIdx + i}</td>
                    <td>
                      <div className="aj-user-name">{u.name}</div>
                      <div className="aj-user-email">{u.email}</div>
                    </td>
                    <td>
                      <span className="aj-trades-badge">{u.totalTrades}</span>
                    </td>
                    <td>
                      <span className={`aj-pnl ${u.totalPnl >= 0 ? 'profit' : 'loss'}`}>
                        {fmtPnl(u.totalPnl)}
                      </span>
                    </td>
                    <td className="aj-date">{fmtDate(u.lastTradeDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="aj-pagination">
              <span className="aj-page-info">
                Page {page} of {totalPages}
              </span>
              <div className="aj-page-btns">
                <button
                  className="aj-page-btn"
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                >
                  <FaChevronLeft />
                </button>

                {/* Page number buttons — show up to 5 */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === 'ellipsis' ? (
                      <span key={`e${i}`} className="aj-ellipsis">…</span>
                    ) : (
                      <button
                        key={p}
                        className={`aj-page-btn${p === page ? ' active' : ''}`}
                        onClick={() => goToPage(p as number)}
                      >
                        {p}
                      </button>
                    )
                  )}

                <button
                  className="aj-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => goToPage(page + 1)}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminJournals;
